import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { MODULE_META, SERVICE_MODULES, fmtDate } from "../../lib/helpers";
import { PageSkeleton, EmptyState } from "../../components/Feedback";
import VisioAgenda from "../../components/VisioAgenda";
import { useAuth } from "../../context/AuthContext";
import * as Icons from "lucide-react";
import { ICONS_BY_NAME } from "../../lib/icons";

// Apparition séquentielle haut->bas au montage initial uniquement (pas de
// rejeu au-delà), léger décalage entre blocs — même patron que le
// soulignement de nav dans Layout.js (transition annulée si
// prefers-reduced-motion, cf. useReducedMotion()).
const DashboardBlock = ({ index, children }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={reduce ? { duration: 0 } : { duration: 0.35, delay: index * 0.08, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
};

const NET_ICON = { instagram: "Instagram", facebook: "Facebook", tiktok: "Music2", linkedin: "Linkedin", youtube: "Youtube", twitter: "Twitter", x: "Twitter", pinterest: "Image", snapchat: "Ghost" };
const socialUrl = (n) => {
  const h = (n.handle || "").trim();
  if (/^https?:\/\//i.test(h)) return h;
  const clean = encodeURIComponent(h.replace(/^@/, ""));
  const net = (n.network || "").toLowerCase();
  if (net.includes("instagram")) return `https://instagram.com/${clean}`;
  if (net.includes("facebook")) return `https://facebook.com/${clean}`;
  if (net.includes("tiktok")) return `https://tiktok.com/@${clean}`;
  if (net.includes("linkedin")) return `https://linkedin.com/company/${clean}`;
  if (net.includes("youtube")) return `https://youtube.com/@${clean}`;
  if (net.includes("twitter") || net === "x") return `https://x.com/${clean}`;
  if (net.includes("pinterest")) return `https://pinterest.com/${clean}`;
  return h ? `https://${encodeURI(h)}` : null;
};

export default function ClientDashboard() {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  const load = () => {
    setLoadError(false);
    api.get("/client/dashboard").then((r) => setData(r.data)).catch(() => setLoadError(true));
  };
  useEffect(load, []);

  if (loadError) {
    return (
      <EmptyState icon="AlertTriangle" title="Impossible de charger votre tableau de bord"
        hint="Une erreur est survenue. Vérifiez votre connexion et réessayez."
        action={<button type="button" onClick={load} data-testid="dashboard-retry"
          className="text-sm bg-marine text-white rounded-sm px-4 py-2 hover:bg-marine-hover transition-colors">Réessayer</button>} />
    );
  }
  if (!data) return <PageSkeleton />;

  const socials = (data.socials || []).filter((n) => n.handle || n.network);
  const quickLinks = data.quick_links || [];
  const brandActive = (data.modules || []).includes("identite_visuelle");

  return (
    <div>
      <PageTitle overline="Votre espace" title={`Bienvenue, ${user.name}`} />

      {(brandActive || quickLinks.length > 0) && (
        <DashboardBlock index={0}>
        <div className="bg-marine rounded-sm p-6 mb-8 relative overflow-hidden" data-testid="client-quick-links">
          <div className="absolute bottom-0 right-0 w-64 h-64 rounded-full bg-bordeaux/40 blur-3xl" />
          <div className="relative z-10">
            <h2 className="font-display text-xl text-white mb-4">Accès rapide</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {brandActive && (
                <button onClick={() => nav("/identite")} data-testid="quick-brand-board"
                  className="flex items-center justify-between gap-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-sm px-4 py-3 transition-colors text-left">
                  <div className="flex items-center gap-3 min-w-0"><Icons.Palette size={16} className="text-white shrink-0" /><div className="min-w-0"><div className="text-sm font-medium text-white truncate">Brand Board</div><div className="text-xs text-white/70 truncate">Votre charte de marque</div></div></div>
                  <Icons.ChevronRight size={16} className="text-white/70 shrink-0" />
                </button>
              )}
              {quickLinks.map((l, i) => (
                <a key={l.id || i} href={l.url} target="_blank" rel="noreferrer" data-testid={`quick-link-${i}`}
                  className="flex items-center justify-between gap-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-sm px-4 py-3 transition-colors">
                  <div className="flex items-center gap-3 min-w-0"><Icons.Link2 size={16} className="text-white shrink-0" /><div className="min-w-0"><div className="text-sm font-medium text-white truncate">{l.name}</div>{l.description && <div className="text-xs text-white/70 truncate">{l.description}</div>}</div></div>
                  <Icons.ExternalLink size={16} className="text-white/70 shrink-0" />
                </a>
              ))}
            </div>
          </div>
        </div>
        </DashboardBlock>
      )}

      {socials.length > 0 && (
        <DashboardBlock index={1}>
        <div className="bg-sable rounded-sm border border-marine/10 shadow-sm p-4 mb-8" data-testid="client-socials">
          <div className="mb-3"><span className="text-xs uppercase tracking-[0.2em] font-semibold text-bordeaux">Vos réseaux sociaux</span></div>
          <div className="flex flex-wrap gap-2">
            {socials.map((n, i) => {
              const url = socialUrl(n);
              const Ic = ICONS_BY_NAME[NET_ICON[(n.network || "").toLowerCase().split(" ")[0]]] || Icons.Globe;
              const content = (
                <>
                  <Ic size={16} className="text-marine" />
                  <span className="text-sm text-marine">{n.network}</span>
                  {n.handle && <span className="text-xs text-marine/80 truncate min-w-0">{n.handle}</span>}
                  {url && <Icons.ExternalLink size={16} className="text-marine" />}
                </>
              );
              return url ? (
                <a key={i} href={url} target="_blank" rel="noreferrer" data-testid={`social-${i}`}
                  className="flex items-center gap-2 border border-marine/15 rounded-full px-4 py-2 hover:border-marine/40 hover:bg-creme transition-all max-w-full min-w-0">{content}</a>
              ) : (
                <div key={i} data-testid={`social-${i}`} className="flex items-center gap-2 border border-marine/15 rounded-full px-4 py-2 max-w-full min-w-0">{content}</div>
              );
            })}
          </div>
        </div>
        </DashboardBlock>
      )}

      <DashboardBlock index={2}>
      <div className="mb-8">
        <VisioAgenda items={data.upcoming_rdvs || []}
          projectPath={(v) => `/projets/${v.project_id}?vue=etapes`}
          missionPath={(v) => (v.project_id ? `/projets/${v.project_id}?vue=etapes` : null)} />
      </div>
      </DashboardBlock>

      <DashboardBlock index={3}>
      <div className="mb-10">
        <div className="bg-sable rounded-sm border border-marine/10 shadow-sm p-6" data-testid="pending-tasks">
          <h2 className="font-display text-xl text-marine mb-4">Tâches en attente</h2>
          {data.pending.length === 0 && <p className="text-sm text-marine/80">Aucune tâche en attente. Tout est à jour.</p>}
          <div className="space-y-2">
            {data.pending.map((t, i) => (
              <button key={i} onClick={() => (t.ticket_id ? window.dispatchEvent(new CustomEvent("open-requests", { detail: { ticketId: t.ticket_id } })) : nav(t.link))} data-testid={`pending-${i}`}
                className="w-full text-left flex items-start gap-3 p-3 rounded-sm border border-marine/10 hover:border-marine/40 hover:bg-creme transition-all">
                <span className="w-2 h-2 rounded-full bg-rouge mt-1.5" />
                <div className="flex-1">
                  <div className="text-sm text-marine">{t.label}</div>
                  {t.project && <div className="text-xs text-marine/80">{t.project}</div>}
                </div>
                <Icons.ChevronRight size={16} className="text-marine" />
              </button>
            ))}
          </div>
        </div>
      </div>
      </DashboardBlock>
    </div>
  );
}
